import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Paper,
} from '@mui/material';
import {
  Dashboard,
  Insights,
  Speed,
  Warning,
  CompareArrows,
  Refresh,
  Psychology,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import InsightCard from '../insights/InsightCard';
import { fetchAllInsights } from '../../services/insightsApi';
import { apiService } from '../../services/api';
import { Insight, InsightCategoryData, CATEGORY_COLORS } from '../../types/insights';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

// Types for trajectory data
interface TrajectoryDataPoint {
  day: number;
  date: string;
  cumulativePlanned: number;
  cumulativeActual: number;
  hasActuals: boolean;
}

interface TrajectoryResponse {
  trajectory: TrajectoryDataPoint[];
  projection: {
    currentDay: number;
    currentVelocity: number;
    projectedWrapDay: number;
    scheduledWrapDay: number;
    varianceDays: number;
    pagesShotToDate: number;
    pagesRemaining: number;
  } | null;
  metadata: {
    totalScriptPages: number;
    totalDays: number;
    currentDay: number;
  };
}

const InsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [insights, setInsights] = useState<InsightCategoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trajectoryData, setTrajectoryData] = useState<TrajectoryResponse | null>(null);
  const [trajectoryLoading, setTrajectoryLoading] = useState(true);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllInsights();
      setInsights(data);
    } catch (err) {
      console.error('Failed to load insights:', err);
      setError('Failed to load insights. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrajectoryData = useCallback(async () => {
    setTrajectoryLoading(true);
    try {
      const response = await apiService.getDriftTrajectoryData();
      if (response.success && response.data) {
        setTrajectoryData(response.data);
      }
    } catch (err) {
      console.error('Failed to load trajectory data:', err);
    } finally {
      setTrajectoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInsights();
    loadTrajectoryData();
  }, [loadInsights, loadTrajectoryData]);

  // Prepare compact chart data
  const prepareChartData = () => {
    if (!trajectoryData?.trajectory) return [];
    
    const velocity = trajectoryData?.projection?.currentVelocity || 0;
    const totalDays = trajectoryData?.metadata?.totalDays || 25;
    
    const chartData = trajectoryData.trajectory.map((point) => ({
      day: point.day,
      planned: point.cumulativePlanned,
      actual: point.hasActuals ? point.cumulativeActual : null,
      projected: null as number | null,
    }));

    // Find the last day with actual data
    const lastActualDay = trajectoryData.trajectory.filter(p => p.hasActuals).slice(-1)[0];
    const lastActualCumulative = lastActualDay?.cumulativeActual || 0;
    const lastActualDayNum = lastActualDay?.day || 0;

    // Calculate projected trajectory
    if (lastActualDayNum > 0 && velocity > 0) {
      for (let day = lastActualDayNum; day <= totalDays; day++) {
        const daysFromLastActual = day - lastActualDayNum;
        const projectedCumulative = lastActualCumulative + (daysFromLastActual * velocity);
        
        const existingPoint = chartData.find(d => d.day === day);
        if (existingPoint) {
          existingPoint.projected = projectedCumulative;
        } else {
          chartData.push({
            day,
            planned: null as any,
            actual: null,
            projected: projectedCumulative,
          });
        }
      }
      chartData.sort((a, b) => a.day - b.day);
    }
    
    return chartData;
  };

  const chartData = prepareChartData();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleExploreWithAI = (prompt: string, precomputedExplanation?: string) => {
    const text = precomputedExplanation || prompt;
    navigate(`/?copilot=${encodeURIComponent(text)}`);
  };

  const categories = [
    { id: 'velocity', label: 'Velocity', icon: <Speed />, color: CATEGORY_COLORS.velocity },
    { id: 'risk', label: 'Risk Indicators', icon: <Warning />, color: CATEGORY_COLORS.risk },
    { id: 'correlation', label: 'Hidden Correlations', icon: <Insights />, color: CATEGORY_COLORS.correlation },
    { id: 'comparative', label: 'Comparative', icon: <CompareArrows />, color: CATEGORY_COLORS.comparative },
  ];

  const getInsightsForCategory = (categoryId: string): Insight[] => {
    if (!insights) return [];
    return (insights as any)[categoryId] || [];
  };

  const getTotalInsightsCount = (): number => {
    if (!insights) return 0;
    return (
      (insights.velocity?.length || 0) +
      (insights.risk?.length || 0) +
      (insights.correlation?.length || 0) +
      (insights.comparative?.length || 0)
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#1A202C' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#2D3748' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 700, color: 'white', mr: 1, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              FilmIQ
            </Typography>
            <Chip
              label="INSIGHTS"
              size="small"
              sx={{
                backgroundColor: '#9F7AEA',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Dashboard">
              <IconButton
                onClick={() => navigate('/')}
                sx={{ color: 'white' }}
              >
                <Dashboard />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh Insights">
              <IconButton
                onClick={loadInsights}
                disabled={loading}
                sx={{ color: 'white' }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Production Velocity Chart - Full Width Header */}
        <Paper
          sx={{
            mx: 3,
            mt: 2,
            mb: 2,
            backgroundColor: '#2D3748',
            borderRadius: 2,
            p: 2,
            border: '1px solid #4A5568',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={600} color="white">
              Production Velocity
            </Typography>
            {trajectoryData?.projection && (
              <Box display="flex" alignItems="center" gap={3}>
                <Chip
                  size="small"
                  icon={trajectoryData.projection.varianceDays > 0 
                    ? <TrendingDown sx={{ color: '#F56565 !important', fontSize: 16 }} />
                    : <TrendingUp sx={{ color: '#48BB78 !important', fontSize: 16 }} />
                  }
                  label={`${trajectoryData.projection.currentVelocity?.toFixed(1) || '—'} pages/day`}
                  sx={{
                    backgroundColor: 'rgba(72, 187, 120, 0.2)',
                    color: '#48BB78',
                    fontWeight: 600,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Day {trajectoryData.projection.currentDay || '—'} of {trajectoryData.metadata?.totalDays || '—'}
                </Typography>
                {/* Legend inline with metrics */}
                <Box display="flex" alignItems="center" gap={2}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 20, height: 2, backgroundColor: '#4299E1', borderStyle: 'dashed' }} />
                    <Typography variant="caption" color="text.secondary">Plan</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 20, height: 2, backgroundColor: '#48BB78' }} />
                    <Typography variant="caption" color="text.secondary">Actual</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 20, height: 2, backgroundColor: '#ED8936' }} />
                    <Typography variant="caption" color="text.secondary">Projected</Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          
          {trajectoryLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={220}>
              <CircularProgress size={28} />
            </Box>
          ) : chartData.length > 0 ? (
            <Box sx={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#718096"
                    tick={{ fill: '#A0AEC0', fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: '#4A5568' }}
                  />
                  <YAxis 
                    stroke="#718096"
                    tick={{ fill: '#A0AEC0', fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: '#4A5568' }}
                    width={40}
                    label={{ value: 'Pages', angle: -90, position: 'insideLeft', fill: '#718096', fontSize: 11 }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#1A202C',
                      border: '1px solid #4A5568',
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: '#E2E8F0' }}
                    formatter={(value) => value != null ? [`${Number(value).toFixed(1)} pages`, ''] : ['—', '']}
                    labelFormatter={(label) => `Day ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="planned"
                    name="Plan"
                    stroke="#4299E1"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    name="Actual"
                    stroke="#48BB78"
                    strokeWidth={2.5}
                    dot={false}
                    connectNulls={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="projected"
                    name="Projected"
                    stroke="#ED8936"
                    strokeWidth={2}
                    strokeDasharray="3 3"
                    dot={false}
                    connectNulls={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height={220}>
              <Typography variant="body2" color="text.secondary">
                No trajectory data available
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Title Section */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="white">
                Production Insights
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Data-driven patterns that reveal why productions drift and what predicts trouble
              </Typography>
            </Box>
            {!loading && (
              <Chip
                icon={<Psychology sx={{ color: 'white !important' }} />}
                label={`${getTotalInsightsCount()} Insights Available`}
                sx={{
                  backgroundColor: '#4A5568',
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>

          {/* Category Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #4A5568',
              '& .MuiTab-root': {
                color: '#A0AEC0',
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48,
                '&.Mui-selected': {
                  color: 'white',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: categories[activeTab]?.color || '#4299E1',
              },
            }}
          >
            {categories.map((category, index) => (
              <Tab
                key={category.id}
                icon={category.icon}
                iconPosition="start"
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {category.label}
                    <Chip
                      label={getInsightsForCategory(category.id).length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: activeTab === index ? category.color : '#4A5568',
                        color: 'white',
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Insights Content */}
        <Box sx={{ flexGrow: 1, minHeight: 350, overflow: 'auto', px: 3, pb: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            categories.map((category, index) => (
              <TabPanel key={category.id} value={activeTab} index={index}>
                <Grid container spacing={3}>
                  {getInsightsForCategory(category.id).length === 0 ? (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 6,
                          color: 'text.secondary',
                        }}
                      >
                        <Insights sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" gutterBottom>
                          No {category.label} Insights Yet
                        </Typography>
                        <Typography variant="body2">
                          Insights will appear as more production data is collected and analyzed.
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    getInsightsForCategory(category.id).map((insight) => (
                      <Grid item xs={12} md={6} lg={4} key={insight.id}>
                        <InsightCard
                          insight={insight}
                          categoryColor={category.color}
                          onExploreWithAI={handleExploreWithAI}
                        />
                      </Grid>
                    ))
                  )}
                </Grid>
              </TabPanel>
            ))
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default InsightsPage;
